import React, { useMemo, useState } from 'react';
import { useData } from '../../store/DataContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { PackagePlusIcon, PlusIcon, SearchIcon } from 'lucide-react';
import { formatCurrency, stockStatus } from './pharmacyUtils';
type MedicineMasterForm = {
  genericName: string;
  brandName: string;
  strength: string;
  dosageForm: string;
  unit: string;
  packSize: string;
  category: string;
  manufacturer: string;
  buyingPrice: string;
  sellingPrice: string;
  reorderLevel: string;
  maximumStock: string;
  status: 'Active' | 'Inactive';
};
const initialForm: MedicineMasterForm = {
  genericName: '',
  brandName: '',
  strength: '',
  dosageForm: 'Tablet',
  unit: 'Tablet',
  packSize: '1',
  category: 'General',
  manufacturer: '',
  buyingPrice: '',
  sellingPrice: '',
  reorderLevel: '20',
  maximumStock: '100',
  status: 'Active'
};
export function InventoryList() {
  const {
    medicines,
    addMedicine
  } = useData();
  const [query, setQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState<MedicineMasterForm>(initialForm);
  const filtered = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return medicines;
    return medicines.filter(medicine => [medicine.drugCode, medicine.medicineId, medicine.genericName, medicine.brandName, medicine.name, medicine.sku, medicine.category, medicine.manufacturer].join(' ').toLowerCase().includes(normalizedQuery));
  }, [medicines, query]);
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const buyingPrice = Number(form.buyingPrice);
    const sellingPrice = Number(form.sellingPrice);
    const reorderLevel = Number(form.reorderLevel);
    const maximumStock = Number(form.maximumStock);
    if (!form.genericName.trim() || !form.brandName.trim() || !form.manufacturer.trim() || !Number.isFinite(buyingPrice) || buyingPrice < 0 || !Number.isFinite(sellingPrice) || sellingPrice < 0) {
      setError('Generic name, brand name, manufacturer, and valid buying and selling prices are required.');
      return;
    }
    if (!Number.isFinite(reorderLevel) || reorderLevel < 0 || !Number.isFinite(maximumStock) || maximumStock < reorderLevel) {
      setError('Enter a valid reorder level and a maximum stock at or above it.');
      return;
    }
    addMedicine({
      genericName: form.genericName.trim(),
      brandName: form.brandName.trim(),
      strength: form.strength.trim() || 'Not specified',
      dosageForm: form.dosageForm,
      unit: form.unit.trim() || form.dosageForm,
      packSize: Math.max(1, Number(form.packSize) || 1),
      category: form.category.trim() || 'General',
      manufacturer: form.manufacturer.trim(),
      buyingPrice,
      sellingPrice,
      currentQuantity: 0,
      reorderLevel: Math.max(0, reorderLevel || 0),
      maximumStock,
      status: form.status
    });
    setForm(initialForm);
    setShowForm(false);
    setError('');
  };
  return <div className="space-y-5" data-id="element-4084">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-4085">
        <div data-id="element-4086">
          <p className="text-sm font-semibold text-pink-600" data-id="element-4087">Medicine master</p>
          <h2 className="mt-1 text-xl font-bold text-slate-950" data-id="element-4088">
            Pharmacy Inventory
          </h2>
          <p className="mt-1 text-sm text-slate-500" data-id="element-4089">
            Drug Code and Medicine ID are created centrally. Live quantities are
            updated by inventory transactions.
          </p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-teal-600 hover:bg-teal-700" data-id="element-4090">
          <PlusIcon className="mr-2 h-4 w-4" data-id="element-4091" /> Add medicine
        </Button>
      </div>

      {showForm && <form onSubmit={submit} className="grid grid-cols-1 gap-4 rounded-xl border border-teal-200 bg-teal-50/50 p-5 md:grid-cols-2 xl:grid-cols-3" data-id="element-4092">
          <div className="flex items-center justify-between xl:col-span-3 md:col-span-2" data-id="element-4093">
            <div data-id="element-4094">
              <h3 className="font-bold text-slate-900" data-id="element-4095">
                New medicine master record
              </h3>
              <p className="mt-1 text-xs text-slate-500" data-id="element-4096">
                System-generated fields: Drug Code and Medicine ID.
              </p>
            </div>
            <button type="button" onClick={() => setShowForm(false)} className="text-sm font-semibold text-slate-500 hover:text-slate-800" data-id="element-4097">
              Cancel
            </button>
          </div>
          {error && <p role="alert" className="text-sm text-red-600 xl:col-span-3 md:col-span-2" data-id="element-4098">
              {error}
            </p>}
          <Input label="Generic name" required value={form.genericName} onChange={event => setForm({
        ...form,
        genericName: event.target.value
      })} data-id="element-4099" />
          <Input label="Brand name" required value={form.brandName} onChange={event => setForm({
        ...form,
        brandName: event.target.value
      })} data-id="element-4100" />
          <Input label="Strength" placeholder="e.g. 500 mg" value={form.strength} onChange={event => setForm({
        ...form,
        strength: event.target.value
      })} data-id="element-4101" />
          <Select label="Dosage form" value={form.dosageForm} onChange={event => setForm({
        ...form,
        dosageForm: event.target.value,
        unit: form.unit === form.dosageForm ? event.target.value : form.unit
      })} options={[{
        value: 'Tablet',
        label: 'Tablet'
      }, {
        value: 'Capsule',
        label: 'Capsule'
      }, {
        value: 'Syrup',
        label: 'Syrup'
      }, {
        value: 'Suspension',
        label: 'Suspension'
      }, {
        value: 'Injection',
        label: 'Injection'
      }, {
        value: 'Cream',
        label: 'Cream'
      }, {
        value: 'Ointment',
        label: 'Ointment'
      }, {
        value: 'Sachet',
        label: 'Sachet'
      }]} data-id="element-4102" />
          <Input label="Unit" placeholder="e.g. tablet, bottle, vial" value={form.unit} onChange={event => setForm({
        ...form,
        unit: event.target.value
      })} data-id="element-4103" />
          <Input label="Pack size" required type="number" min="1" value={form.packSize} onChange={event => setForm({
        ...form,
        packSize: event.target.value
      })} data-id="element-4104" />
          <Input label="Category" value={form.category} onChange={event => setForm({
        ...form,
        category: event.target.value
      })} data-id="element-4105" />
          <Input label="Manufacturer" required value={form.manufacturer} onChange={event => setForm({
        ...form,
        manufacturer: event.target.value
      })} data-id="element-4106" />
          <Select label="Status" value={form.status} onChange={event => setForm({
        ...form,
        status: event.target.value as MedicineMasterForm['status']
      })} options={[{
        value: 'Active',
        label: 'Active'
      }, {
        value: 'Inactive',
        label: 'Inactive'
      }]} data-id="element-4107" />
          <Input label="Buying price (ZMW)" required type="number" min="0" step="0.01" value={form.buyingPrice} onChange={event => setForm({
        ...form,
        buyingPrice: event.target.value
      })} data-id="element-4108" />
          <Input label="Selling price (ZMW)" required type="number" min="0" step="0.01" value={form.sellingPrice} onChange={event => setForm({
        ...form,
        sellingPrice: event.target.value
      })} data-id="element-4109" />
          <Input label="Reorder level" required type="number" min="0" value={form.reorderLevel} onChange={event => setForm({
        ...form,
        reorderLevel: event.target.value
      })} data-id="element-4110" />
          <Input label="Maximum stock" required type="number" min="0" value={form.maximumStock} onChange={event => setForm({
        ...form,
        maximumStock: event.target.value
      })} data-id="element-4111" />
          <div className="flex items-end justify-end xl:col-span-3 md:col-span-2" data-id="element-4112">
            <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-id="element-4113">
              <PackagePlusIcon className="mr-2 h-4 w-4" data-id="element-4114" /> Create medicine
            </Button>
          </div>
        </form>}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-4115">
        <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-4116">
          <div className="relative w-full max-w-xl" data-id="element-4117">
            <SearchIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" data-id="element-4118" />
            <input aria-label="Search medicine master" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search code, name, brand, category or manufacturer" className="w-full rounded-md border border-slate-300 py-2 pl-9 pr-3 text-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600" data-id="element-4119" />
          </div>
          <p className="shrink-0 text-sm text-slate-500" data-id="element-4120">
            {filtered.length} of {medicines.length} medicines
          </p>
        </div>
        <div className="overflow-x-auto" data-id="element-4121">
          <table className="min-w-[1250px] divide-y divide-slate-100" data-id="element-4122">
            <thead className="bg-slate-50" data-id="element-4123">
              <tr data-id="element-4124">
                {['Drug Code', 'Medicine', 'Pack & Manufacturer', 'Stock', 'Price', 'Created / updated', 'Record status'].map(heading => <th key={heading} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-4125">
                    {heading}
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-4126">
              {filtered.map(medicine => <tr key={medicine.id} className="align-top" data-id="element-4127">
                  <td className="px-5 py-3.5" data-id="element-4128">
                    <p className="font-mono text-sm font-semibold text-teal-800" data-id="element-4129">
                      {medicine.drugCode}
                    </p>
                    <p className="mt-1 text-xs text-slate-500" data-id="element-4130">
                      {medicine.medicineId}
                    </p>
                  </td>
                  <td className="px-5 py-3.5" data-id="element-4131">
                    <p className="font-semibold text-slate-900" data-id="element-4132">
                      {medicine.genericName}
                    </p>
                    <p className="mt-0.5 text-sm text-slate-600" data-id="element-4133">
                      {medicine.brandName}
                    </p>
                    <p className="mt-1 text-xs text-slate-500" data-id="element-4134">
                      {medicine.strength} · {medicine.dosageForm}
                    </p>
                  </td>
                  <td className="px-5 py-3.5 text-sm text-slate-600" data-id="element-4135">
                    <p data-id="element-4136">
                      {medicine.packSize} {medicine.unit}
                      {medicine.packSize === 1 ? '' : 's'}
                    </p>
                    <p className="mt-1 text-xs text-slate-500" data-id="element-4137">
                      {medicine.category} · {medicine.manufacturer}
                    </p>
                  </td>
                  <td className="px-5 py-3.5" data-id="element-4138">
                    <p className="font-semibold text-slate-900" data-id="element-4139">
                      {medicine.currentQuantity} on hand
                    </p>
                    <p className="mt-1 text-xs text-slate-500" data-id="element-4140">
                      Reorder {medicine.reorderLevel} · Max{' '}
                      {medicine.maximumStock}
                    </p>
                  </td>
                  <td className="px-5 py-3.5 text-sm text-slate-600" data-id="element-4141">
                    <p data-id="element-4142">Buy {formatCurrency(medicine.buyingPrice)}</p>
                    <p className="mt-1 font-medium text-slate-900" data-id="element-4143">
                      Sell {formatCurrency(medicine.sellingPrice)}
                    </p>
                  </td>
                  <td className="px-5 py-3.5 text-xs text-slate-500" data-id="element-4144">
                    <p data-id="element-4145">Created {formatRecordDate(medicine.createdAt)}</p>
                    <p className="mt-1" data-id="element-4146">
                      Updated {formatRecordDate(medicine.updatedAt)}
                    </p>
                  </td>
                  <td className="px-5 py-3.5" data-id="element-4147">
                    <Status value={stockStatus(medicine)} data-id="element-4148" />
                    <p className={`mt-2 text-xs font-semibold ${medicine.status === 'Active' ? 'text-emerald-700' : 'text-slate-500'}`} data-id="element-4149">
                      {medicine.status}
                    </p>
                  </td>
                </tr>)}
              {!filtered.length && <tr data-id="element-4150">
                  <td colSpan={7} className="px-5 py-14 text-center text-sm text-slate-500" data-id="element-4151">
                    No medicines match your search.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}
function formatRecordDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Not available' : new Intl.DateTimeFormat('en-ZM', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).format(date);
}
function Status({
  value
}: {
  value: string;
}) {
  const classes = value === 'Out of stock' ? 'bg-red-50 text-red-700' : value === 'Low stock' ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700';
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${classes}`} data-id="element-4152">
      {value}
    </span>;
}